import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import '../styles/RegistrationModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function RegistrationModal({ city, onClose, onSuccess }) {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    full_address: city.fullAddress || city.address || '',
    city: city.city || city.name || '',
    country: city.country || '',
    role: 'Participant',
    companion_count: '',
    consent_accepted: false,
    referral_code: city.referralCode || '',
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      // Clear companion_count when role changes to City Organizer
      if (name === 'role' && value === 'City Organizer') {
        updated.companion_count = '';
      }
      return updated;
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
    // Clear companion_count error when role changes
    if (name === 'role' && errors.companion_count) {
      setErrors((prev) => ({
        ...prev,
        companion_count: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.full_address) {
      newErrors.full_address = 'Full address is required';
    }

    if (!formData.city) {
      newErrors.city = 'City is required';
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    if (!formData.consent_accepted) {
      newErrors.consent_accepted = 'You must accept the consent';
    }

    if (formData.role === 'Participant' && formData.companion_count !== '') {
      const count = parseInt(formData.companion_count);
      if (isNaN(count) || count < 0) {
        newErrors.companion_count = 'Companion count must be a positive number';
      } else if (count > 10) {
        newErrors.companion_count = 'Companion count must be less than or equal to 10';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        email: formData.email,
        full_address: formData.full_address,
        city: formData.city,
        country: formData.country,
        companion_count: formData.role === 'Participant' && formData.companion_count !== '' ? parseInt(formData.companion_count) : 0,
        consent_accepted: formData.consent_accepted,
        referral_code: formData.referral_code || null,
        role: formData.role,
      };

      const response = await axios.post(`${API_BASE_URL}/auth/register`, payload, {
        withCredentials: true,
      });

      if (response.data.success) {
        // Refresh auth state to get the new user data
        await checkAuth();
        // Close modal and redirect to dashboard
        onClose();
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.error || 'Registration failed. Please try again.';
      
      // If it's an email already taken error, show it on the email field
      if (error.response?.status === 409 && errorMessage.toLowerCase().includes('email')) {
        setErrors({
          email: 'Email already taken',
          submit: errorMessage,
        });
      } else {
        setErrors({
          submit: errorMessage,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" onClick={onClose}>
          ×
        </button>
        <h2>Register for {city.name}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="your.email@example.com"
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <span className="error-message">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="full_address">Full Address *</label>
            <input
              type="text"
              id="full_address"
              name="full_address"
              value={formData.full_address}
              onChange={handleChange}
              className={errors.full_address ? 'error' : ''}
            />
            {errors.full_address && <span className="error-message">{errors.full_address}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="city">City *</label>
            <input
              type="text"
              id="city"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className={errors.city ? 'error' : ''}
            />
            {errors.city && <span className="error-message">{errors.city}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="country">Country *</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country}
              onChange={handleChange}
              className={errors.country ? 'error' : ''}
            />
            {errors.country && <span className="error-message">{errors.country}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="role">Role *</label>
            <select
              id="role"
              name="role"
              value={formData.role}
              onChange={handleChange}
              className={errors.role ? 'error' : ''}
            >
              <option value="Participant">Participant</option>
              <option value="City Organizer">City Organizer</option>
            </select>
            {errors.role && <span className="error-message">{errors.role}</span>}
          </div>

          {formData.role === 'Participant' && (
            <div className="form-group">
              <label htmlFor="companion_count">Companion Count (Optional)</label>
              <input
                type="number"
                id="companion_count"
                name="companion_count"
                value={formData.companion_count}
                onChange={handleChange}
                min="0"
                max="10"
                placeholder="0"
                className={errors.companion_count ? 'error' : ''}
              />
              {errors.companion_count && <span className="error-message">{errors.companion_count}</span>}
            </div>
          )}

          <div className="form-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="consent_accepted"
                checked={formData.consent_accepted}
                onChange={handleChange}
                className={errors.consent_accepted ? 'error' : ''}
              />
              <span>I accept the terms and conditions *</span>
            </label>
            {errors.consent_accepted && <span className="error-message">{errors.consent_accepted}</span>}
          </div>

          {errors.submit && <div className="error-message submit-error">{errors.submit}</div>}

          <button type="submit" className="submit-button" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default RegistrationModal;

